import { Typography } from '@mui/material'
import dayjs from 'dayjs'
import { last } from 'lodash'
import { useState } from 'react'
import { useAcceptTermsAndConditionsMutation } from '~/common/generated/apollo-operations'
import { useGlobalContext } from '~/common/hooks/useGlobalContext'
import { useTypedTranslation } from '~/common/hooks/useTypedTranslation'
import { Button } from './ui/atoms/button/Button'
import { Modal } from './ui/molecules/modal/Modal'

// Update log for the terms and conditions. If the user last accepted the terms and conditions
// before the date in the update log, they will be required to re-accept it
const UPDATE_LOG = [
  {
    date: dayjs('1-30-2024'),
    reason: 'Added terms of service',
  },
  {
    date: dayjs('1-2-2025'),
    reason: 'Added e-sign provision',
  },
  {
    date: dayjs('4-25-2025'),
    reason: 'Updated policy for opt-in communications and 3rd party messaging',
  },
]

/**
 * If a user has not accepted the policies, show a dialog that requires them to accept the terms
 * and conditions before proceeding.
 */
export function TermsAndConditionsDialog() {
  const { t } = useTypedTranslation()
  const { user } = useGlobalContext()
  const [open, setOpen] = useState(true)
  const [acceptTermsAndConditions] = useAcceptTermsAndConditionsMutation()

  if (!user) {
    return <></>
  }

  const lastAccepted = dayjs(user.policiesAcceptedAt)
  const requiresReaccept = last(UPDATE_LOG)?.date.isAfter(lastAccepted)

  let starterText = ''
  if (user.policiesAcceptedAt) {
    if (requiresReaccept) {
      starterText = t('terms_and_conditions.starter_text')
    } else {
      return <></>
    }
  }

  const handleAccept = () => {
    acceptTermsAndConditions().then(() => {
      setOpen(false)
    })
  }

  return (
    <Modal
      actions={
        <Button onClick={handleAccept} variant="secondary">
          {t('terms_and_conditions.accept')}
        </Button>
      }
      open={open}
      showCancelButton={false}
      title={t('terms_and_conditions.terms_and_conditions')}
    >
      <div className="flex flex-row gap-1">
        <Typography variant="body2">
          {starterText} {t('terms_and_conditions.proceeding_text')}
        </Typography>
        <Typography className="text-blue-7 underline" variant="body2">
          <a href="https://www.doss.com/terms-and-conditions" rel="noreferrer" target="_blank">
            {t('terms_and_conditions.terms_and_conditions')}
          </a>
        </Typography>
        <Typography variant="body2">{t('terms_and_conditions.and')}</Typography>
        <Typography className="text-blue-7 underline" variant="body2">
          <a href="https://www.doss.com/privacy-policy" rel="noreferrer" target="_blank">
            {t('terms_and_conditions.privacy_policy')}
          </a>
        </Typography>
      </div>
    </Modal>
  )
}
